import React from 'react';
import { useTranslation } from 'react-i18next';
import { Play, XCircle, Loader2, FileText, Users, Mic, Shuffle, PhoneOff, PhoneMissed } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';

import { OngoingCallAction, type CallSessionResponse } from '../types/wizard.types';
import MicrophoneSetupModal from '@ui/shared/Dialer/MicrophoneSetupModal.tsx';

interface ActiveSessionPromptProps {
  activeSession: CallSessionResponse;
  onResume: () => void;
  onEnd: () => void;
  isEnding: boolean;
}

export default function ActiveSessionPrompt ({
  activeSession,
  onResume,
  onEnd,
  isEnding,
}: ActiveSessionPromptProps): React.JSX.Element {
  const { t } = useTranslation();

  const isPaused = activeSession.status === 'paused';

  // Extract contact list title from contactList array if available
  const contactListTitle = activeSession.contactListTitle ?? activeSession.contactList?.[0]?.title ?? null;

  // Format queue info
  const queueInfo = activeSession.queueLength !== undefined
    ? activeSession.totalContacts !== undefined
      ? `${activeSession.queueLength} / ${activeSession.totalContacts}`
      : `${activeSession.queueLength}`
    : null;

  return (
    <div className="relative h-full">
      <MicrophoneSetupModal />
      <div className="mx-auto max-w-2xl">
        <Card className="p-6 rounded-2xl shadow-lg border border-amber-200 bg-amber-50/50">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Play className="h-8 w-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl">
              {t('wizard.activeSession.title')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('wizard.activeSession.description')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Session Info */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('wizard.activeSession.status')}
                </span>
                <Badge
                  variant={isPaused ? 'secondary' : 'default'}
                  className={isPaused ? '' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}
                >
                  {isPaused
                    ? t('wizard.activeSession.statusPaused')
                    : t('wizard.activeSession.statusActive')
                  }
                </Badge>
              </div>

              {/* Intent */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  {t('wizard.activeSession.intent')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={activeSession.scriptTitle ?? ''}>
                  {activeSession.scriptTitle ?? '—'}
                </span>
              </div>

              {/* Contact List */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {t('wizard.activeSession.contactList')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={contactListTitle ?? ''}>
                  {contactListTitle ?? '—'}
                </span>
              </div>

              {/* Voicemail */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mic className="h-4 w-4" />
                  {t('wizard.activeSession.voicemail')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={activeSession.voicemailTitle ?? ''}>
                  {activeSession.voicemailTitle ?? '—'}
                </span>
              </div>

              {/* Queue Length */}
              {queueInfo !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('wizard.activeSession.queueLength')}
                  </span>
                  <span className="text-sm font-medium">
                    {queueInfo} {t('wizard.activeSession.contacts')}
                  </span>
                </div>
              )}

              {/* Shuffle Contacts */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shuffle className="h-4 w-4" />
                  {t('wizard.activeSession.shuffle')}
                </span>
                <span className="text-sm font-medium">
                  {activeSession.random === true
                    ? t('wizard.activeSession.shuffleOn')
                    : t('wizard.activeSession.shuffleOff')
                  }
                </span>
              </div>

              {/* Ongoing Calls Behavior */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {activeSession.ongoingCallAction === OngoingCallAction.CUT_OFF ? (
                    <PhoneOff className="h-4 w-4" />
                  ) : (
                    <PhoneMissed className="h-4 w-4" />
                  )}
                  {t('wizard.activeSession.ongoingCalls')}
                </span>
                <span className="text-sm font-medium">
                  {activeSession.ongoingCallAction === OngoingCallAction.CUT_OFF
                    ? t('wizard.activeSession.cutOffMode')
                    : t('wizard.activeSession.holdMode')
                  }
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                size="lg"
                onClick={onResume}
                disabled={isEnding}
              >
                <Play className="mr-2 h-4 w-4" />
                {t('wizard.activeSession.resume')}
              </Button>

              <Button
                variant="outline"
                className="flex-1 border border-destructive text-destructive hover:bg-destructive/10"
                size="lg"
                onClick={onEnd}
                disabled={isEnding}
              >
                {isEnding ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                {isEnding
                  ? t('wizard.activeSession.ending')
                  : t('wizard.activeSession.endAndCreate')
                }
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {t('wizard.activeSession.hint')}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
